import type { TFunction } from "i18next";
import { OPTIVAL_CONSTANTS } from "../../../shared/Constants";
import type {
  DatasetOptionUi,
  DocumentRowUi,
  PreviewItemUi,
} from "../types/documentManagementUiTypes";
import { uploadBasename } from "../utils/uploadFileUtils";

export interface RagUploadApiItem {
  document_id: number;
  file_name: string;
  upload_status: string;
  mime_type?: string;
  detected_company?: string;
  detected_product?: string;
  detected_model?: string;
  company_name?: string;
  product_name?: string;
  model_name?: string;
  document_category?: string | null;
  metadata_status?: string;
  metadata_job_id?: number;
}

export interface RagUploadFailureApiItem {
  file_name: string;
  error?: string;
  message?: string;
  matching_file_name?: string | null;
}

export interface RagUploadApiResponse {
  documents?: RagUploadApiItem[];
  /** Present when server resolved the dataset (including lazy-created). */
  dataset?: { id: number; name: string; description?: string | null };
  failures?: RagUploadFailureApiItem[];
  uploaded_count?: number;
  failed_count?: number;
}

export type UploadFailureDisplay = {
  fileName: string;
  reason: string;
  matchingFileName?: string | null;
};

function matchingFileNameFromFailure(
  failure: RagUploadFailureApiItem
): string | null {
  const matching = failure.matching_file_name?.trim();
  return matching || null;
}

/** Short label for in-panel failure list (not for toast). */
export function mapUploadFailureDisplay(
  failure: RagUploadFailureApiItem,
  t: TFunction
): UploadFailureDisplay {
  const fileName = failure.file_name?.trim() || "Unknown file";
  const matchingFileName = matchingFileNameFromFailure(failure);

  // Structured content duplicate, plus transitional older backends that still
  // send error=duplicate with matching_file_name populated.
  if (
    failure.error === "content_duplicate" ||
    (failure.error === "duplicate" && matchingFileName)
  ) {
    return {
      fileName,
      reason: t("opva.dmUploadContentDuplicate", {
        defaultValue: "Identical content already exists in this dataset.",
      }),
      matchingFileName,
    };
  }
  if (failure.error === "duplicate") {
    return {
      fileName,
      reason: t("opva.dmUploadFilenameDuplicate", {
        defaultValue: "Already in dataset",
      }),
    };
  }
  if (failure.error === "read_failed") {
    return { fileName, reason: "Could not read file" };
  }
  const message = failure.message?.trim();
  if (message && message.length <= 120) {
    return { fileName, reason: message };
  }
  if (message && message.length > 120) {
    return { fileName, reason: `${message.slice(0, 117)}…` };
  }
  return { fileName, reason: "Upload failed" };
}

export function mapUploadFailuresDisplay(
  failures: RagUploadFailureApiItem[],
  t: TFunction
): UploadFailureDisplay[] {
  return failures.map((failure) => mapUploadFailureDisplay(failure, t));
}

export type RagDatasetApiItem = DatasetOptionUi;

export interface RagConfirmIngestionItem {
  document_id: number;
  company_name: string;
  product_name: string;
  model_name: string;
  document_category: string;
}

export interface RagIngestionConfirmation {
  document_id: number | null;
  job_id?: number;
  ingestion_enqueued: boolean;
  file_name?: string;
  error?: string;
}

export interface RagConfirmIngestionResponse {
  confirmations: RagIngestionConfirmation[];
}

export interface RagBulkDeleteResultItem {
  document_id: number;
  file_name?: string;
  ok: boolean;
  error?: string;
}

export interface RagBulkDeleteResponse {
  results: RagBulkDeleteResultItem[];
  deleted_count: number;
  failed_count: number;
}

export interface RagBulkDeleteRequestBody {
  document_ids: number[];
}

export interface RagDocumentListApiItem {
  id: number;
  original_file_name: string;
  mime_type?: string;
  dataset_id: number;
  dataset_name: string;
  upload_status: string;
  lifecycle_status?: string;
  processing_error?: string | null;
  last_processing_job_id?: number | null;
  retry_count?: number;
  is_active: boolean;
  role_access: string;
  company_name: string;
  product_name: string;
  model_name: string;
  document_category: string;
  summary: string;
  metadata_status?: string;
}

export interface RagDocumentMetadataStatusItem {
  document_id: number;
  original_file_name?: string;
  mime_type?: string;
  metadata_status: string;
  company_name?: string;
  product_name?: string;
  model_name?: string;
}

export interface RagDocumentMetadataStatusResponse {
  items: RagDocumentMetadataStatusItem[];
}

export const METADATA_STATUS = {
  NOT_APPLICABLE: "not_applicable",
  PENDING: "pending",
  DONE: "done",
  FAILED: "failed",
} as const;

export function isMetadataStatusPending(status?: string): boolean {
  const normalized = status?.trim().toLowerCase();
  return (
    normalized === METADATA_STATUS.PENDING ||
    normalized === "queued" ||
    normalized === "running"
  );
}

export function isMetadataStatusTerminal(status?: string): boolean {
  if (!status?.trim()) {
    return true;
  }
  const normalized = status.trim().toLowerCase();
  return (
    normalized === METADATA_STATUS.DONE ||
    normalized === METADATA_STATUS.FAILED ||
    normalized === METADATA_STATUS.NOT_APPLICABLE
  );
}

export function metadataStatusNeedsWait(items: { metadata_status?: string }[]): boolean {
  return items.some((item) => isMetadataStatusPending(item.metadata_status));
}

export function previewItemsNeedMetadataWait(items: PreviewItemUi[]): boolean {
  return items.some(
    (item) => item.metadata_pending || isMetadataStatusPending(item.metadata_status)
  );
}

/** True when any preview row is tied to a metadata job (SSE path, not bulk status poll). */
export function previewItemsUseMetadataJobStream(items: PreviewItemUi[]): boolean {
  return items.some(
    (item) => item.metadata_job_id != null && item.metadata_job_id > 0
  );
}

export function isMetadataJobTerminal(event: IngestJobEventUi | null | undefined): boolean {
  const status = event?.status?.trim().toLowerCase();
  return status === "completed" || status === "failed";
}

export function isMetadataJobActive(job: IngestJobUi): boolean {
  return !isMetadataJobTerminal(job.last);
}

export interface RagDocumentListResponse {
  items: RagDocumentListApiItem[];
}

export interface IngestJobEventUi {
  job_id?: number;
  document_id?: number;
  status?: string;
  phase?: string;
  progress_pct?: number;
  error_message?: string;
  elapsed_ms?: number;
  /** Authoritative media duration from worker probe (videos). */
  duration_seconds?: number | null;
  error?: string;
}

export interface IngestJobUi {
  jobId: number;
  fileName: string;
  /** Set for metadata jobs so session can rehydrate after refresh. */
  documentId?: number;
  last: IngestJobEventUi | null;
}

/** Synthetic terminal event when the document was hard-deleted after a content failure. */
export function buildRemovedDocumentIngestionJobEvent(
  jobId: number,
  documentId?: number
): IngestJobEventUi {
  return {
    job_id: jobId,
    document_id: documentId,
    status: "failed",
    phase: "rejected",
    progress_pct: 0,
    error_message: "INGESTION_REMOVED_EMPTY_CONTENT",
  };
}

export function isRemovedDocumentIngestionJob(
  event: IngestJobEventUi | null | undefined
): boolean {
  const msg = event?.error_message?.trim() ?? "";
  return (
    event?.phase === "rejected" ||
    msg === "INGESTION_REMOVED_EMPTY_CONTENT" ||
    msg.toLowerCase().includes("no readable text and was removed")
  );
}

export const LIFECYCLE_STATUS = {
  UPLOADED: "uploaded",
  PROCESSING: "processing",
  PROCESSED: "processed",
  FAILED: "failed",
  ROLLED_BACK: "rolled_back",
} as const;

export function resolveLifecycleStatus(row: {
  lifecycle_status?: string;
  upload_status: string;
}): string {
  if (row.lifecycle_status?.trim()) {
    return row.lifecycle_status.trim().toLowerCase();
  }
  const normalized = row.upload_status.trim().toLowerCase();
  if (
    normalized === "stored" ||
    normalized === "completed" ||
    normalized === "ingested" ||
    normalized === "ready"
  ) {
    return LIFECYCLE_STATUS.PROCESSED;
  }
  if (
    normalized === "chunking" ||
    normalized === "embedding" ||
    normalized === "processing"
  ) {
    return LIFECYCLE_STATUS.PROCESSING;
  }
  return normalized;
}

export function isDocumentProcessed(
  uploadStatus: string,
  lifecycleStatus?: string
): boolean {
  const life = lifecycleStatus || resolveLifecycleStatus({ upload_status: uploadStatus });
  return life === LIFECYCLE_STATUS.PROCESSED;
}

export function isDocumentProcessing(
  uploadStatus: string,
  lifecycleStatus?: string
): boolean {
  const life = lifecycleStatus || resolveLifecycleStatus({ upload_status: uploadStatus });
  return life === LIFECYCLE_STATUS.PROCESSING;
}

export function isDocumentFailed(
  uploadStatus: string,
  lifecycleStatus?: string
): boolean {
  const life = lifecycleStatus || resolveLifecycleStatus({ upload_status: uploadStatus });
  return life === LIFECYCLE_STATUS.FAILED;
}

export function isDocumentRolledBack(
  uploadStatus: string,
  lifecycleStatus?: string
): boolean {
  const life = lifecycleStatus || resolveLifecycleStatus({ upload_status: uploadStatus });
  return life === LIFECYCLE_STATUS.ROLLED_BACK;
}

export function isDocumentFailedOrRolledBack(
  uploadStatus: string,
  lifecycleStatus?: string
): boolean {
  return (
    isDocumentFailed(uploadStatus, lifecycleStatus) ||
    isDocumentRolledBack(uploadStatus, lifecycleStatus)
  );
}

export function canReconfirmIngestion(
  uploadStatus: string,
  lifecycleStatus?: string
): boolean {
  const life = lifecycleStatus || resolveLifecycleStatus({ upload_status: uploadStatus });
  return (
    life === LIFECYCLE_STATUS.UPLOADED || life === LIFECYCLE_STATUS.ROLLED_BACK
  );
}

/** Documents awaiting user review before ingestion is started. */
export const PENDING_REVIEW_UPLOAD_STATUS = "uploaded";

export function isPendingReviewUpload(uploadStatus: string): boolean {
  return uploadStatus.trim().toLowerCase() === PENDING_REVIEW_UPLOAD_STATUS;
}

const INGEST_PIPELINE_STATUSES = new Set([
  "processing",
  "chunking",
  "embedding",
]);

export function isDocumentIngestionPipeline(
  uploadStatus: string,
  lifecycleStatus?: string
): boolean {
  const life = lifecycleStatus || resolveLifecycleStatus({ upload_status: uploadStatus });
  if (life === LIFECYCLE_STATUS.PROCESSING) {
    return true;
  }
  return INGEST_PIPELINE_STATUSES.has(uploadStatus.trim().toLowerCase());
}

export function isIngestJobTerminal(job: IngestJobUi): boolean {
  const status = job.last?.status?.trim().toLowerCase();
  return status === "completed" || status === "failed";
}

export function isIngestJobActive(job: IngestJobUi): boolean {
  return !isIngestJobTerminal(job);
}

export function summarizeIngestJobs(jobs: IngestJobUi[]): {
  completed: number;
  failed: number;
  total: number;
} {
  let completed = 0;
  let failed = 0;
  for (const job of jobs) {
    const status = job.last?.status?.trim().toLowerCase();
    if (status === "completed") {
      completed += 1;
    } else if (status === "failed") {
      failed += 1;
    }
  }
  return { completed, failed, total: jobs.length };
}

export function summarizeValidatedDocuments(
  documentIds: number[],
  documents: { id: number; upload_status: string; lifecycle_status?: string }[]
): { processed: number; failed: number; removed: number; total: number } {
  let processed = 0;
  let failed = 0;
  let removed = 0;
  for (const id of documentIds) {
    const row = documents.find((d) => d.id === id);
    if (!row) {
      removed += 1;
      continue;
    }
    if (isDocumentProcessed(row.upload_status, row.lifecycle_status)) {
      processed += 1;
    } else if (isDocumentFailedOrRolledBack(row.upload_status, row.lifecycle_status)) {
      failed += 1;
    }
  }
  return { processed, failed, removed, total: documentIds.length };
}

type IngestJobOutcome = "processed" | "failed" | "removed";

function classifyIngestJobOutcome(
  job: IngestJobUi,
  documents: { id: number; upload_status: string; lifecycle_status?: string }[]
): IngestJobOutcome | null {
  if (!isIngestJobTerminal(job)) {
    return null;
  }
  if (isRemovedDocumentIngestionJob(job.last)) {
    return "removed";
  }

  const jobStatus = job.last?.status?.trim().toLowerCase();
  const id = job.documentId ?? job.last?.document_id;
  const row =
    id != null && id > 0 ? documents.find((d) => d.id === id) : undefined;

  if (jobStatus === "failed") {
    return row ? "failed" : "removed";
  }

  if (jobStatus === "completed") {
    if (row && isDocumentFailedOrRolledBack(row.upload_status, row.lifecycle_status)) {
      return "failed";
    }
    return "processed";
  }

  if (!row) {
    return "removed";
  }
  if (isDocumentProcessed(row.upload_status, row.lifecycle_status)) {
    return "processed";
  }
  if (isDocumentFailedOrRolledBack(row.upload_status, row.lifecycle_status)) {
    return "failed";
  }
  return null;
}

/** Merge terminal job signals with document list state for the ingestion-done summary. */
export function summarizeIngestionOutcomes(
  jobs: IngestJobUi[],
  documents: { id: number; upload_status: string; lifecycle_status?: string }[]
): { processed: number; failed: number; removed: number; total: number } {
  if (!jobs.length) {
    return summarizeValidatedDocuments(
      documents.map((d) => d.id),
      documents
    );
  }

  let processed = 0;
  let failed = 0;
  let removed = 0;

  for (const job of jobs) {
    const outcome = classifyIngestJobOutcome(job, documents);
    if (outcome === "processed") {
      processed += 1;
    } else if (outcome === "failed") {
      failed += 1;
    } else if (outcome === "removed") {
      removed += 1;
    }
  }

  return { processed, failed, removed, total: jobs.length };
}

export function ingestJobsAwaitDocumentValidation(
  jobs: IngestJobUi[],
  documents: { id: number; upload_status: string; lifecycle_status?: string }[]
): boolean {
  const terminalJobs = jobs.filter((job) => isIngestJobTerminal(job));
  if (!terminalJobs.length) {
    return false;
  }
  return terminalJobs.some((job) => {
    if (isRemovedDocumentIngestionJob(job.last)) {
      return false;
    }
    const id = job.documentId ?? job.last?.document_id;
    if (id == null || id <= 0) {
      return false;
    }
    const row = documents.find((d) => d.id === id);
    if (!row) {
      // Hard-deleted after permanent content failure — terminal job is resolved.
      return false;
    }
    const life = resolveLifecycleStatus(row);
    return (
      life !== LIFECYCLE_STATUS.PROCESSED &&
      life !== LIFECYCLE_STATUS.FAILED &&
      life !== LIFECYCLE_STATUS.ROLLED_BACK
    );
  });
}

/** Remove preview rows that successfully started ingestion. */
export function previewItemsAfterConfirm(
  items: PreviewItemUi[],
  confirmations: RagIngestionConfirmation[]
): PreviewItemUi[] | null {
  const enqueuedIds = new Set(
    confirmations
      .filter((c) => c.ingestion_enqueued && c.document_id != null)
      .map((c) => c.document_id as number)
  );
  const next = items.filter((p) => !enqueuedIds.has(p.document_id));
  return next.length ? next : null;
}

/** Keep in-session review edits when merging server metadata updates. */
export function preservePreviewSessionEdits(
  existing: PreviewItemUi,
  incoming: PreviewItemUi
): PreviewItemUi {
  const edited = existing.metadata_edited ?? {};
  return {
    ...incoming,
    metadata_job_id: existing.metadata_job_id ?? incoming.metadata_job_id,
    document_category: existing.document_category || incoming.document_category,
    company_name: edited.company_name
      ? existing.company_name
      : incoming.company_name,
    product_name: edited.product_name
      ? existing.product_name
      : incoming.product_name,
    model_name: edited.model_name ? existing.model_name : incoming.model_name,
    metadata_edited: existing.metadata_edited,
    detected_company: existing.detected_company || incoming.detected_company,
    detected_product: existing.detected_product || incoming.detected_product,
    detected_model: existing.detected_model || incoming.detected_model,
  };
}

export function applyMetadataSnapshotToPreviewItems(
  items: PreviewItemUi[],
  snapshot: RagDocumentMetadataStatusResponse
): PreviewItemUi[] {
  const byId = new Map(
    (snapshot.items ?? []).map((row) => [row.document_id, row])
  );
  return items.map((item) => {
    const row = byId.get(item.document_id);
    if (!row) {
      return item;
    }
    const status = row.metadata_status ?? item.metadata_status;
    const edited = item.metadata_edited ?? {};
    return {
      ...item,
      metadata_status: status,
      metadata_pending: isMetadataStatusPending(status),
      company_name: edited.company_name
        ? item.company_name
        : row.company_name?.trim() || item.company_name,
      product_name: edited.product_name
        ? item.product_name
        : row.product_name?.trim() || item.product_name,
      model_name: edited.model_name
        ? item.model_name
        : row.model_name?.trim() || item.model_name,
    };
  });
}

export function mapDocumentRowToPreview(row: DocumentRowUi): PreviewItemUi {
  const fileName = row.original_file_name;
  const mime =
    row.mime_type ||
    (fileName.toLowerCase().endsWith(".pdf")
      ? "application/pdf"
      : "application/octet-stream");
  const metadataPending = isMetadataStatusPending(row.metadata_status);
  return {
    document_id: row.id,
    file_name: fileName,
    mime_type: mime,
    metadata_status: row.metadata_status,
    metadata_pending: metadataPending,
    dataset_id: row.dataset_id,
    dataset_name: row.dataset_name,
    company_name: row.company_name || "",
    product_name: row.product_name || "",
    model_name: row.model_name || "",
    document_category: row.document_category || "",
    detected_company: "",
    detected_product: "",
    detected_model: "",
  };
}

export function previewItemsFromPendingDocuments(
  documents: DocumentRowUi[]
): PreviewItemUi[] {
  return documents
    .filter((row) => isPendingReviewUpload(row.upload_status))
    .map(mapDocumentRowToPreview);
}

/** Server list is authoritative for membership; keep in-session category edits. */
export function mergePreviewItems(
  current: PreviewItemUi[] | null,
  incoming: PreviewItemUi[]
): PreviewItemUi[] {
  if (!incoming.length) {
    return [];
  }
  const byId = new Map(
    (current ?? []).map((item) => [item.document_id, item])
  );
  return incoming.map((item) => {
    const existing = byId.get(item.document_id);
    if (!existing) {
      return item;
    }
    return preservePreviewSessionEdits(existing, item);
  });
}

export function mapUploadItemToPreview(
  item: RagUploadApiItem,
  dataset: Pick<DatasetOptionUi, "id" | "name">
): PreviewItemUi {
  const company = item.company_name || item.detected_company || "";
  const product = item.product_name || item.detected_product || "";
  const model = item.model_name || item.detected_model || "";
  const mime =
    item.mime_type ||
    (item.file_name.toLowerCase().endsWith(".pdf")
      ? "application/pdf"
      : "application/octet-stream");
  const status = item.metadata_status;
  return {
    document_id: item.document_id,
    file_name: item.file_name,
    mime_type: mime,
    metadata_status: status,
    metadata_job_id: item.metadata_job_id,
    metadata_pending: isMetadataStatusPending(status),
    dataset_id: dataset.id,
    dataset_name: dataset.name,
    company_name: company,
    product_name: product,
    model_name: model,
    document_category: item.document_category || "",
    detected_company: item.detected_company || "",
    detected_product: item.detected_product || "",
    detected_model: item.detected_model || "",
  };
}

export function mapDocumentListItem(item: RagDocumentListApiItem): DocumentRowUi {
  return {
    id: item.id,
    original_file_name: item.original_file_name,
    mime_type: item.mime_type || "",
    dataset_id: item.dataset_id,
    dataset_name: item.dataset_name || "",
    upload_status: item.upload_status,
    lifecycle_status: resolveLifecycleStatus(item),
    processing_error: item.processing_error ?? undefined,
    last_processing_job_id: item.last_processing_job_id ?? undefined,
    retry_count: item.retry_count ?? 0,
    is_active: item.is_active,
    role_access: item.role_access || '["*"]',
    company_name: item.company_name || "",
    product_name: item.product_name || "",
    model_name: item.model_name || "",
    document_category: item.document_category || "",
    summary: item.summary || "",
    metadata_status: item.metadata_status,
  };
}

/** Must match Instrakt {@code RagDocumentLifecycleService.MAX_BULK_DELETE_BATCH}. */
export const RAG_BULK_DELETE_MAX_BATCH = 50;

export function chunkArray<T>(items: T[], size: number): T[][] {
  if (!items.length) return [];
  const batchSize = size > 0 ? size : items.length;
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += batchSize) {
    chunks.push(items.slice(i, i + batchSize));
  }
  return chunks;
}

export function buildBulkDeleteBody(documentIds: number[]): RagBulkDeleteRequestBody {
  return { document_ids: documentIds };
}

export function buildConfirmIngestionBody(
  items: PreviewItemUi[]
): RagConfirmIngestionItem[] {
  return items.map((p) => ({
    document_id: p.document_id,
    company_name: p.company_name,
    product_name: p.product_name,
    model_name: p.model_name,
    document_category: p.document_category,
  }));
}

export function buildReconfirmIngestionBodyFromDocument(
  row: DocumentRowUi
): RagConfirmIngestionItem[] {
  return buildConfirmIngestionBody([mapDocumentRowToPreview(row)]);
}

export function mapConfirmEnqueueFailures(
  confs: RagIngestionConfirmation[]
): { fileName: string; error: string }[] {
  return confs
    .filter((c) => !c.ingestion_enqueued && c.error)
    .map((c) => ({
      fileName: c.file_name || "file",
      error: c.error || "",
    }));
}

export function ingestionJobStreamUrl(jobId: number): string {
  const base = (process.env.REACT_APP_OPVA_API_BASE_URL || "").replace(/\/?$/, "/");
  const token = encodeURIComponent(localStorage.getItem("jwtToken") || "");
  return `${base}rag/ingestion/jobs/${jobId}/stream?token=${token}`;
}

export function ingestionJobStatusUrl(jobId: number): string {
  return `rag/ingestion/jobs/${jobId}`;
}

export function metadataJobStreamUrl(jobId: number): string {
  const base = (process.env.REACT_APP_OPVA_API_BASE_URL || "").replace(/\/?$/, "/");
  const token = encodeURIComponent(localStorage.getItem("jwtToken") || "");
  return `${base}rag/metadata/jobs/${jobId}/stream?token=${token}`;
}

export function metadataJobStatusUrl(jobId: number): string {
  return `rag/metadata/jobs/${jobId}`;
}

export function buildUploadFormData(params: {
  datasetId?: number;
  newDatasetName?: string;
  newDatasetDescription?: string;
  files: File[];
  roleAccess: string[];
  companyName?: string;
  productName?: string;
  modelName?: string;
  documentCategory?: string;
}): FormData {
  const form = new FormData();
  const newName = params.newDatasetName?.trim();
  if (newName) {
    form.append("newDatasetName", newName);
    if (params.newDatasetDescription?.trim()) {
      form.append("newDatasetDescription", params.newDatasetDescription.trim());
    }
  } else if (params.datasetId != null && params.datasetId > 0) {
    form.append("datasetId", String(params.datasetId));
  }
  form.append("roleAccess", JSON.stringify(params.roleAccess));
  if (params.companyName?.trim()) {
    form.append("companyName", params.companyName.trim());
  }
  if (params.productName?.trim()) {
    form.append("productName", params.productName.trim());
  }
  if (params.modelName?.trim()) {
    form.append("modelName", params.modelName.trim());
  }
  if (params.documentCategory?.trim()) {
    form.append("documentCategory", params.documentCategory.trim());
  }
  for (const file of params.files) {
    form.append("files", file, uploadBasename(file));
  }
  return form;
}

export function buildRoleAccessPatchBody(roleAccess: string[]): { role_access: string[] } {
  return { role_access: roleAccess };
}

export type RagDocumentMetadataPatchBody = {
  company_name?: string;
  product_name?: string;
  model_name?: string;
  summary?: string;
};

export function buildMetadataPatchBody(
  form: RagDocumentMetadataPatchBody
): RagDocumentMetadataPatchBody {
  return {
    company_name: form.company_name,
    product_name: form.product_name,
    model_name: form.model_name,
    summary: form.summary,
  };
}

export const RAG_API = {
  datasets: OPTIVAL_CONSTANTS.API_URLS.RAG_DATASETS,
  dataset: (datasetId: number) => `rag/datasets/${datasetId}`,
  upload: OPTIVAL_CONSTANTS.API_URLS.RAG_DOCUMENTS_UPLOAD,
  documents: OPTIVAL_CONSTANTS.API_URLS.RAG_DOCUMENTS,
  confirmIngestion: OPTIVAL_CONSTANTS.API_URLS.RAG_CONFIRM_INGESTION,
  documentsBulkDelete: OPTIVAL_CONSTANTS.API_URLS.RAG_DOCUMENTS_BULK_DELETE,
  document: (documentId: number) => `rag/documents/${documentId}`,
  documentRoleAccess: (documentId: number) =>
    OPTIVAL_CONSTANTS.API_URLS.RAG_DOCUMENT_ROLE_ACCESS(documentId),
  documentMetadata: (documentId: number) =>
    OPTIVAL_CONSTANTS.API_URLS.RAG_DOCUMENT_METADATA(documentId),
  documentDisable: (documentId: number) =>
    OPTIVAL_CONSTANTS.API_URLS.RAG_DOCUMENT_DISABLE(documentId),
  documentEnable: (documentId: number) =>
    OPTIVAL_CONSTANTS.API_URLS.RAG_DOCUMENT_ENABLE(documentId),
  documentCategory: (documentId: number) =>
    `rag/documents/${documentId}/category`,
  documentProcessingStatus: (documentId: number) =>
    `rag/documents/${documentId}/processing-status`,
  ingestionJobStream: (jobId: number) => ingestionJobStreamUrl(jobId),
  ingestionJobStatus: (jobId: number) => ingestionJobStatusUrl(jobId),
  metadataJobStream: (jobId: number) => metadataJobStreamUrl(jobId),
  metadataJobStatus: (jobId: number) => metadataJobStatusUrl(jobId),
} as const;
